export const contractActivityConstante = Object.freeze({
  TYPE_PROFESSIONAL: 1,
  TYPE_PEDAGOGICAL: 2,
});

// Miroir de App\Enum\Contract\ActivityWorkflowStatusEnum (src/Enum/Contract/ActivityWorkflowStatusEnum.php).
export const activityWorkflowStatusConstante = Object.freeze({
  DRAFT: 1,
  VALIDATED: 2,
  CERTIFIED: 3,
  CANCELLED: 4,
});

// Statuts workflow "actifs" (hors archivage) : les seuls que le back accepte encore quand ils sont
// résolus par soft delete (cf. activityViewModelManager.activeStatusesListParams()) — CANCELLED est
// délibérément exclu, une activité annulée n'étant chargée que via le paramètre dédié `cancelled=1`.
export const activityActiveWorkflowStatusesConstante = Object.freeze([
  activityWorkflowStatusConstante.DRAFT,
  activityWorkflowStatusConstante.VALIDATED,
  activityWorkflowStatusConstante.CERTIFIED,
]);

// Miroir de App\Enum\Contract\ActivityTemporalStatusEnum (src/Enum/Contract/ActivityTemporalStatusEnum.php).
export const activityTemporalStatusConstante = Object.freeze({
  ONGOING: 1,
  FINISHED: 2,
});

// Niveau d'avancement exigé par le back pour valider une activité : ContractActivityService::
// isValidationEligible() n'autorise la transition qu'à 100 % (RG2.2V2.2, les 4 premiers onglets
// complets) et renvoie sinon un 422.
export const activityValidationRequiredLevelConstante = 100;

// Source unique de l'éligibilité à la validation, partagée par le bouton « Signer » de l'onglet
// validation et par le raccourci « Enregistrer et valider » du pied de modale (ST154-TA-4908) :
// les deux sont masqués tant que cette condition n'est pas remplie, jamais seulement l'un des deux.
export const isActivityValidationEligible = (activity) =>
  activity?.levelOfAchievement === activityValidationRequiredLevelConstante;

// Miroir de App\Enum\Contract\ActivityWorkflowStepEnum (src/Enum/Contract/ActivityWorkflowStepEnum.php).
export const activityWorkflowStepConstante = Object.freeze({
  CREATION: 1,
  DESCRIPTION: 2,
  ANALYSIS: 3,
  SKILLS: 4,
  VALIDATION: 5,
});

// Étapes ordonnées de la frise de progression d'une activité : source unique partagée entre la
// frise (ActivityWorkflowStepperComponent) et le badge d'étape du bloc Activité.
export const activityWorkflowStepsConstante = Object.freeze([
  { value: activityWorkflowStepConstante.CREATION, label: "studea_manager.questionnaire.activity_step_creation" },
  { value: activityWorkflowStepConstante.DESCRIPTION, label: "studea_manager.questionnaire.activity_step_description" },
  { value: activityWorkflowStepConstante.ANALYSIS, label: "studea_manager.questionnaire.activity_step_analysis" },
  { value: activityWorkflowStepConstante.SKILLS, label: "studea_manager.questionnaire.activity_step_skills" },
  { value: activityWorkflowStepConstante.VALIDATION, label: "studea_manager.questionnaire.activity_step_validation" },
]);

// Axe "complétude" des filtres du bloc Activité (statut workflow) : Toutes / À compléter (DRAFT) /
// Complètes (VALIDATED ou CERTIFIED). Archivées (CANCELLED) reste exclusif des autres valeurs, à
// l'identique du filtre miroir côté Mission (missionStatusFilterConstante.ARCHIVED) : une activité
// annulée sort des vues actives, et l'axe chronologie ne lui est plus appliqué (équivalent front
// de cancelled=1, qui ignore temporalStatus côté back).
// "Certifiées" est un sous-ensemble strict de "Complètes" (activityCompletedWorkflowStatusesConstante
// couvre VALIDATED et CERTIFIED) : les deux chips coexistent volontairement.
export const activityCompletionFilterConstante = Object.freeze({
  ALL: "all",
  TO_COMPLETE: "to_complete",
  COMPLETED: "completed",
  CERTIFIED: "certified",
  ARCHIVED: "archived",
  // Valeur dédiée au clic sur l'alerte/le badge "à certifier" (ST154-TA-4911, retour de revue
  // MR!1704) : restreinte au seul VALIDATED, contrairement à "Complètes" qui couvre aussi CERTIFIED
  // (activityCompletedWorkflowStatusesConstante). Volontairement absente de
  // activityCompletionFiltersConstante ci-dessous : ce n'est pas un filtre sélectionnable au clic
  // sur un chip, seulement une cible de navigation programmatique cohérente avec le décompte
  // affiché (DashboardView.getContractActivitiesToCertifyCount).
  TO_CERTIFY: "to_certify",
});

// Axe "chronologie" des filtres du bloc Activité (statut temporel calculé), indépendant de l'axe
// complétude (RG2.2V2.1) : une activité peut être à la fois En cours et À compléter. Il n'existe
// pas de valeur "toutes" : l'axe est toujours contraint, En cours par défaut.
export const activityChronologyFilterConstante = Object.freeze({
  ONGOING: "ongoing",
  FINISHED: "finished",
});

// Options du bloc de filtres rapides du bloc Activité (axe complétude), partagées entre
// QuestionnaireActivityComponent (aperçu questionnaire) et TriadDashboardActivityTabComponent
// (onglet Activité du dashboard) : les deux vues doivent exposer des filtres identiques (TA-4895).
export const activityCompletionFiltersConstante = Object.freeze([
  { value: activityCompletionFilterConstante.ALL, label: "studea_manager.questionnaire.activity_filter_all" },
  {
    value: activityCompletionFilterConstante.TO_COMPLETE,
    label: "studea_manager.questionnaire.activity_filter_to_complete",
  },
  {
    value: activityCompletionFilterConstante.COMPLETED,
    label: "studea_manager.questionnaire.activity_filter_completed",
  },
  {
    value: activityCompletionFilterConstante.CERTIFIED,
    label: "studea_manager.questionnaire.activity_filter_certified",
  },
  {
    value: activityCompletionFilterConstante.ARCHIVED,
    label: "studea_manager.questionnaire.activity_filter_archived",
  },
]);

// Options du bloc de filtres rapides du bloc Activité (axe chronologie), même principe que
// activityCompletionFiltersConstante ci-dessus.
export const activityChronologyFiltersConstante = Object.freeze([
  { value: activityChronologyFilterConstante.ONGOING, label: "studea_manager.questionnaire.activity_filter_ongoing" },
  { value: activityChronologyFilterConstante.FINISHED, label: "studea_manager.questionnaire.activity_filter_finished" },
]);

// "Complètes" est le miroir exact de "À compléter" sur l'axe workflow : l'activité est sortie du
// brouillon, signée par l'alternant (VALIDATED) et éventuellement certifiée par le MAP (CERTIFIED).
export const activityCompletedWorkflowStatusesConstante = Object.freeze([
  activityWorkflowStatusConstante.VALIDATED,
  activityWorkflowStatusConstante.CERTIFIED,
]);

export const activitySortConstante = Object.freeze({
  START_DATE: "startDate",
  END_DATE: "endDate",
});

// Seuils d'affichage front des scores de compétence, sans contrepartie backend.
export const skillScoreDisplayConstante = Object.freeze({
  MIN: 40,
  SUCCESS_MIN: 70,
});

// Échelle d'évaluation par compétence d'une activité. Les niveaux 0 à 4 sont toujours acceptés par
// PUT /validate et /certify (RG2.2V2.4) : EXPERT n'est masqué que côté présentation, quand la
// formation ne l'active pas (RG2.2V2.5, cf. TrainingCourse::isActivityExpertLevelEnabled() exposé
// par le champ informatif activityExpertLevelEnabled de l'onglet "validation").
export const activityEvaluationLevelConstante = Object.freeze({
  EXPERT: 4,
});
